export type PasswordRule = {
  id: string;
  label: string;
  test: (value: string) => boolean;
};

/** AC: "maximum 64 characters". Shared with the input's own `maxLength` (110533) so entry is
 *  blocked at the 65th keystroke instead of only failing validation after the fact. */
export const PASSWORD_MAX_LENGTH = 64;

/** AC: "minimum 8 characters". Paired with PASSWORD_MAX_LENGTH in the single length rule below. */
export const PASSWORD_MIN_LENGTH = 8;

/**
 * Order and wording are the design's (110539, Figma `12:3164`): five rules, with the two length
 * bounds read as one line rather than as separate min/max rows.
 */
export const PASSWORD_RULES: PasswordRule[] = [
  { id: 'lower', label: 'At least one lowercase letter', test: (v) => /[a-z]/.test(v) },
  { id: 'upper', label: 'At least one uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { id: 'special', label: 'At least one special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
  { id: 'digit', label: 'At least one number', test: (v) => /[0-9]/.test(v) },
  {
    id: 'length',
    label: `Min ${PASSWORD_MIN_LENGTH} characters and Max ${PASSWORD_MAX_LENGTH} characters`,
    test: (v) => v.length >= PASSWORD_MIN_LENGTH && v.length <= PASSWORD_MAX_LENGTH,
  },
];

export type PasswordRuleResult = { id: string; label: string; met: boolean };

export function evaluatePasswordRules(value: string): PasswordRuleResult[] {
  return PASSWORD_RULES.map((rule) => ({ id: rule.id, label: rule.label, met: rule.test(value) }));
}

export function isPasswordValid(value: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(value));
}
